/**
 * Cross-tab serialization for /auth/refresh calls.
 *
 * The API's refresh-token rotation revokes the old token atomically and
 * fires a "theft detected" response if a previously-rotated token is
 * presented again (see api/src/modules/auth/auth.service.ts:201-272). Two
 * tabs racing on boot-time refresh would both send the original cookie
 * value, the loser would match a revoked hash, and the API would revoke
 * the entire token family for the user.
 *
 * `navigator.locks.request('gh-auth-refresh', { mode: 'exclusive' }, fn)`
 * serializes the callback across same-origin tabs. By the time Tab B's
 * callback runs, Tab A has already updated the refreshToken cookie, so B
 * presents the fresh value.
 *
 * Browsers without the Web Locks API (very rare today, ~9% globally) fall
 * back to running the callback immediately — single-tab dedupe in apiFetch
 * still applies, and those users are no worse off than they would be on
 * the Terminal pattern this replaces.
 */
export async function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  if (typeof navigator !== 'undefined' && 'locks' in navigator && navigator.locks) {
    return await navigator.locks.request('gh-auth-refresh', { mode: 'exclusive' }, async () => {
      return await fn();
    });
  }
  return await fn();
}
